import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { REDACTED, SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import { PGAnalyzeIntegrationFormUpdateIntegrationMutation } from "./__generated__/PGAnalyzeIntegrationFormUpdateIntegrationMutation.graphql";
import { PGAnalyzeIntegrationFormValidateIntegrationMutation } from "./__generated__/PGAnalyzeIntegrationFormValidateIntegrationMutation.graphql";

const updateIntegrationMutation = graphql`
  mutation PGAnalyzeIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation PGAnalyzeIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface PGAnalyzeIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  onDisconnect?: () => void;
  integrationId?: string;
  initialApiKey?: string;
  initialOrganizationSlug?: string;
  initialBaseUrl?: string;
}

export default function PGAnalyzeIntegrationForm({
  onComplete,
  onCancel,
  onDisconnect,
  integrationId,
  initialApiKey,
  initialOrganizationSlug,
  initialBaseUrl,
}: PGAnalyzeIntegrationFormProps) {
  const [apiKey, setApiKey] = useState(initialApiKey ? REDACTED : "");
  const [organizationSlug, setOrganizationSlug] = useState(initialOrganizationSlug || "");
  const [baseUrl, setBaseUrl] = useState(initialBaseUrl || "");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<PGAnalyzeIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<PGAnalyzeIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  const buildCredentials = () => {
    const creds: Record<string, string> = {
      api_key: apiKey.trim(),
      organization_slug: organizationSlug.trim(),
    };
    if (baseUrl.trim()) {
      creds.base_url = baseUrl.trim();
    }
    return creds;
  };

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }
    if (!organizationSlug.trim()) {
      setFormError("Organization Slug is required");
      return;
    }

    const input = {
      integrationType: "PGANALYZE" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(integrationId && { integrationId }),
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          return;
        }

        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }

        setValidationSuccess("Credentials validated successfully!");
        setIsValidated(true);
      },
      onError: error => {
        setFormError(error.message);
      },
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }
    if (!organizationSlug.trim()) {
      setFormError("Organization Slug is required");
      return;
    }

    const input = {
      integrationType: "PGANALYZE" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(integrationId && { integrationId }),
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      },
    });
  };

  const resetValidation = () => {
    setIsValidated(false);
    setValidationSuccess(null);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">PGAnalyze Configuration</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key" className="text-muted-foreground">API Key</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="api-key"
                    value={apiKey}
                    fieldLabel="API Key"
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      resetValidation();
                    }}
                    placeholder="Enter your PGAnalyze API Key"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization-slug" className="text-muted-foreground">Organization Slug</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="organization-slug"
                    type="text"
                    value={organizationSlug}
                    onChange={(e) => {
                      setOrganizationSlug(e.target.value);
                      resetValidation();
                    }}
                    placeholder="Enter your PGAnalyze organization slug"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="base-url" className="text-muted-foreground">
                Base URL <span className="text-xs">(optional, for Enterprise Server)</span>
              </Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="base-url"
                    type="text"
                    value={baseUrl}
                    onChange={(e) => {
                      setBaseUrl(e.target.value);
                      resetValidation();
                    }}
                    placeholder="https://app.pganalyze.com"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      <div className="flex items-center pt-2">
        {onDisconnect && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onDisconnect}
            disabled={isMutationInFlight || isValidationInFlight}
          >
            Disconnect
          </Button>
        )}
        <div className="flex ml-auto space-x-3">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
